import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trFold, SearchClear, statusesForZone, statusColorCls, hubsForZone, useSaleStatuses, saleStatusCls, prodStatusCls, KpiCard, areaNum } from "@/components/shared";
import { MultiFilter, DesignCell, useHiddenCols, ColumnsMenu, AreaFilterButtons, ProductionStatusCell, ProductionStatusAddDialog, useColOrder, OrderedCells, useResizableCols, usePinnedCols } from "@/pages/admin/ProjectsPage";
import { toast } from "sonner";
import { LogOut, Search, Factory, FolderOpen, Ruler, Box, Boxes, Undo2, ArrowUp, ArrowDown, ArrowUpDown, FileSpreadsheet, Camera, ShoppingCart } from "lucide-react";
import { isImpersonating, endImpersonation } from "@/lib/impersonate";
import { PROGRESS_ITEMS, progressPct, KurulumDateCell } from "@/pages/AssignerPage";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ListChecks } from "lucide-react";

const zoneCls = {
  "Blue Zone": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Green Zone": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

export default function UreticiPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [hubs, setHubs] = useState([]);
  const [q, setQ] = useState("");
  const [zoneSel, setZoneSel] = useState([]);
  const [hubSel, setHubSel] = useState([]);
  const [stSel, setStSel] = useState([]);
  const [saleSel, setSaleSel] = useState([]);
  const [standTypeSel, setStandTypeSel] = useState([]);
  const [producerSel, setProducerSel] = useState([]);
  const [prodStSel, setProdStSel] = useState([]);
  const [areaSel, setAreaSel] = useState([]);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [hiddenCols, setHiddenCols] = useHiddenCols("cop31_uretici_hidden_cols");
  const v = (k) => !hiddenCols.includes(k);
  const cls = (k, base) => (v(k) ? base : `${base} hidden`);
  const saleStatuses = useSaleStatuses();
  const myZones = user?.zones || [];
  const baskiOnly = ["username", "contact_email", "email"].some((k) => (user?.[k] || "").toLowerCase() === "gz1graphics@yrdistanbul.com");
  const [prodStatuses, setProdStatuses] = useState([]);
  const [prodStatusAddFor, setProdStatusAddFor] = useState(null);
  const [progressFor, setProgressFor] = useState(null);
  const isMine = (p) => p.producer_id === user?.id;

  const toggleProgress = async (p, item) => {
    const cur = p.kurulum_progress || [];
    const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
    const ordered = PROGRESS_ITEMS.filter((x) => next.includes(x));
    const apply = (val) => {
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, kurulum_progress: val } : x)));
      setProgressFor((pf) => (pf && pf.id === p.id ? { ...pf, kurulum_progress: val } : pf));
    };
    apply(ordered);
    try {
      await api.patch(`/uretici/projects/${p.id}`, { kurulum_progress: ordered });
    } catch (e) {
      toast.error(apiError(e));
      apply(cur);
    }
  };

  const saveKurulumDate = async (p, key, t) => {
    const prev = p.kurulum_tarihi || "";
    setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, kurulum_tarihi: t } : x)));
    try {
      await api.patch(`/uretici/projects/${p.id}`, { kurulum_tarihi: t });
      toast.success(t ? `${p.name} → ${t}` : `${p.name}: kurulum tarihi kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, kurulum_tarihi: prev } : x)));
    }
  };

  useEffect(() => {
    api.get("/uretici/projects").then((r) => { setRows(r.data); setLoaded(true); }).catch(() => setLoaded(true));
    api.get("/settings/hubs").then((r) => setHubs(r.data.hubs || [])).catch(() => {});
    api.get("/settings/production-statuses").then((r) => setProdStatuses(r.data.statuses || [])).catch(() => {});
  }, []);

  const setProductionStatus = async (p, val) => {
    try {
      await api.patch(`/uretici/projects/${p.id}`, { production_status: val });
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, production_status: val || null } : x)));
      toast.success(val ? `${p.name} → ${val}` : `${p.name}: üretim durumu kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const zones = [...new Set(rows.map((r) => r.zone))].sort();
  const term = trFold(q.trim());
  const tabBase = rows.filter((r) => !term || [r.name, r.stand_no, r.zone, r.project_type, r.hub].some((x) => trFold(x).includes(term)));
  const STAND_TYPES = ["Custom", "Template", "Customized Template"];
  const FM = {
    area: (p) => areaSel.length === 0 || areaSel.includes(areaNum(p.area_m2)),
    zone: (p) => zoneSel.length === 0 || zoneSel.includes(p.zone),
    hub: (p) => hubSel.length === 0 || hubSel.includes(p.hub || "__none__"),
    st: (p) => stSel.length === 0 || stSel.includes(p.project_status || "__none__"),
    sale: (p) => saleSel.length === 0 || saleSel.includes(p.sale_status || "__none__"),
    type: (p) => standTypeSel.length === 0 || standTypeSel.includes(p.stand_type || "__none__"),
    producer: (p) => producerSel.length === 0 || producerSel.includes(p.producer_id || "__none__"),
    prodSt: (p) => prodStSel.length === 0 || prodStSel.includes(p.production_status || "__none__"),
  };
  const baseFor = (except) => tabBase.filter((p) => Object.entries(FM).every(([k, fn]) => k === except || fn(p)));
  const zoneFacet = baseFor("zone");
  const hubFacet = baseFor("hub");
  const stFacet = baseFor("st");
  const saleFacet = baseFor("sale");
  const typeFacet = baseFor("type");
  const producerFacet = baseFor("producer");
  const producerOpts = [...new Map(producerFacet.filter((p) => p.producer_id).map((p) => [p.producer_id, p.producer_name || "Üretici"])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], "tr", { sensitivity: "base" }));
  const prodStFacet = baseFor("prodSt");
  const areaFacet = baseFor("area");
  const areaOptions = [...new Set(areaFacet.map((p) => areaNum(p.area_m2)).filter((n) => n > 0))]
    .sort((a, b) => a - b)
    .map((n) => ({ value: n, count: areaFacet.filter((p) => areaNum(p.area_m2) === n).length }));
  const visible = baseFor(null);
  const hubOptions = zoneSel.length ? [...new Set(zoneSel.flatMap((z) => hubsForZone(hubs, z)))] : [...new Set(zones.flatMap((z) => hubsForZone(hubs, z)))];
  const statusOptions = [...new Set((zoneSel.length ? zoneSel : zones).flatMap((z) => statusesForZone(z)))];

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortVal = (p) => {
    switch (sort.key) {
      case "zone": return p.zone || "";
      case "stand_no": return p.stand_no || "";
      case "area": return areaNum(p.area_m2);
      case "type": return p.project_type || "";
      case "stand_type": return p.stand_type || "";
      case "tasarim": return (p.files || []).length;
      case "uretim": return (p.uretim_files || []).length;
      case "baski": return (p.baski_files || []).length;
      case "icmal": return (p.icmal_files || []).length;
      case "status": return p.project_status || "";
      case "sale": return p.sale_status || "";
      case "hub": return p.hub || "";
      case "producer": return p.producer_name || "";
      case "prod_status": return p.production_status || "";
      case "kurulum": return (p.kurulum_tarihi || "").split(".").reverse().join("");
      case "progress": return (p.kurulum_progress || []).length;
      default: return p.name || "";
    }
  };
  const sorted = [...visible].sort((a, b) => {
    const va = sortVal(a), vb = sortVal(b);
    const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "tr", { numeric: true, sensitivity: "base" });
    return sort.dir === "asc" ? cmp : -cmp;
  });
  const SortTh = ({ k, children, hide }) => hide ? null : (
    <th className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => toggleSort(k)}
        data-testid={`uretici-sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors duration-150 hover:text-slate-900 ${sort.key === k ? "text-slate-900" : ""}`}
      >
        {children}
        {sort.key === k ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="text-slate-300" />}
      </button>
    </th>
  );

  const URETICI_COLS = [
    { key: "zone", label: "Bölge" }, { key: "stand_no", label: "Stand" }, { key: "area", label: "m²" },
    { key: "type", label: "Tür" }, { key: "stand_type", label: "Stand Türü" }, { key: "tasarim", label: "Tasarım" },
    { key: "uretim", label: "Üretim" }, { key: "baski", label: "Baskı" }, { key: "icmal", label: "İcmal" },
    { key: "status", label: "Statü" }, { key: "hub", label: "Hub" }, { key: "sale", label: "Süreç Durum" },
    { key: "producer", label: "Üretici" }, { key: "prod_status", label: "Üretim Durumu" },
    { key: "kurulum", label: "Kurulum Tarihi" }, { key: "progress", label: "İlerleme" },
  ].filter((c) => !baskiOnly || !["tasarim", "uretim", "icmal", "prod_status"].includes(c.key));
  const [colOrder, setColOrder] = useColOrder("cop31_uretici_col_order", URETICI_COLS.map((c) => c.key));
  const tableRef = useRef(null);
  useResizableCols(tableRef, "cop31_uretici_col_widths");
  usePinnedCols(tableRef, "cop31_uretici_col_pins");

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const data = sorted.map((p, i) => ({
      "#": i + 1,
      "Proje Adı": p.name || "",
      "Bölge": p.zone || "",
      "Stand No": p.stand_no || "",
      "Alan (m²)": areaNum(p.area_m2) || "",
      "Tür": p.project_type || "",
      "Stand Türü": p.stand_type || "",
      "Statü": p.project_status || "",
      "Hub": p.hub || "",
      "Süreç Durum": p.sale_status || "",
      "Üretici": p.producer_name || "",
      "Üretim Durumu": p.production_status || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [{ wch: 4 }, { wch: 40 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 22 }, { wch: 18 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Projeler");
    XLSX.writeFile(wb, `projeler-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${data.length} proje Excel'e aktarıldı`);
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="uretici-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1700px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><Factory size={10} /> Üretici</div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:flex items-center gap-2" data-testid="uretici-name">
              {user?.contact_name}
              {myZones.map((z) => <Badge key={z} variant="outline" className={`rounded-none text-[10px] ${zoneCls[z] || ""}`}>{z}</Badge>)}
            </span>
            {isImpersonating() && (
              <button onClick={endImpersonation} data-testid="uretici-end-impersonation" className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors duration-200">
                <Undo2 size={14} /> Yönetici Hesabına Dön
              </button>
            )}
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="uretici-logout" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
              <LogOut size={14} /> Çıkış Yap
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1700px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Projeler</h1>
            <p className="text-sm text-slate-500 mt-1">{myZones.join(" · ")} — {rows.length} proje</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje, stand ara..." data-testid="uretici-search" className="rounded-none border-slate-200 h-10 pl-9 pr-9 w-56 bg-white" />
              <SearchClear value={q} onClear={() => setQ("")} testid="uretici-search-clear" />
            </div>
            <ColumnsMenu columns={URETICI_COLS} hidden={hiddenCols} onChange={setHiddenCols} order={colOrder} onReorder={setColOrder} testid="uretici-columns-menu" />
            <Button variant="outline" onClick={() => navigate("/saha-fotograflari")} data-testid="uretici-field-photos-link" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
              <Camera size={15} /> Saha Foto
            </Button>
            <Button variant="outline" onClick={() => navigate("/siparisler")} data-testid="uretici-orders-link" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
              <ShoppingCart size={15} /> Siparişler
            </Button>
            <Button variant="outline" onClick={exportExcel} data-testid="uretici-export-excel" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
              <FileSpreadsheet size={15} /> Excel
            </Button>
          </div>
        </div>

        <div className="flex items-end gap-3 flex-wrap" data-testid="uretici-filters">
          {zones.length > 1 && (
            <MultiFilter
              label="Bölge"
              testid="uretici-zone-select"
              totalCount={zoneFacet.length}
              selected={zoneSel}
              onChange={(nv) => { setZoneSel(nv); setHubSel([]); setStSel([]); }}
              options={zones.map((z) => ({ value: z, label: z, count: zoneFacet.filter((p) => p.zone === z).length }))}
            />
          )}
          {hubOptions.length > 0 && (
            <MultiFilter
              label="Hub"
              testid="uretici-hub-filter"
              width="w-[200px]"
              totalCount={hubFacet.length}
              selected={hubSel}
              onChange={setHubSel}
              options={[
                ...hubOptions.map((h) => ({ value: h, label: h, count: hubFacet.filter((p) => p.hub === h).length })),
                { value: "__none__", label: "Atanmamış", count: hubFacet.filter((p) => !p.hub).length },
              ]}
            />
          )}
          <MultiFilter
            label="Statü"
            testid="uretici-status-filter"
            width="w-[180px]"
            totalCount={stFacet.length}
            selected={stSel}
            onChange={setStSel}
            options={[
              ...statusOptions.map((st) => ({ value: st, label: st, count: stFacet.filter((p) => p.project_status === st).length })),
              { value: "__none__", label: "Atanmamış", count: stFacet.filter((p) => !p.project_status).length },
            ]}
          />
          <MultiFilter
            label="Süreç Durum"
            testid="uretici-sale-filter"
            width="w-[200px]"
            totalCount={saleFacet.length}
            selected={saleSel}
            onChange={setSaleSel}
            options={[
              ...saleStatuses.map((s) => ({ value: s, label: s, count: saleFacet.filter((p) => p.sale_status === s).length })),
              { value: "__none__", label: "Atanmamış", count: saleFacet.filter((p) => !p.sale_status).length },
            ]}
          />
          <MultiFilter
            label="Stand Türü"
            testid="uretici-stand-type-filter"
            totalCount={typeFacet.length}
            selected={standTypeSel}
            onChange={setStandTypeSel}
            options={[
              ...STAND_TYPES.map((t) => ({ value: t, label: t, count: typeFacet.filter((p) => p.stand_type === t).length })),
              { value: "__none__", label: "Atanmamış", count: typeFacet.filter((p) => !p.stand_type).length },
            ]}
          />
          <MultiFilter
            label="Üretici"
            testid="uretici-producer-filter"
            width="w-[190px]"
            totalCount={producerFacet.length}
            selected={producerSel}
            onChange={setProducerSel}
            options={[
              ...producerOpts.map(([id, name]) => ({ value: id, label: name, count: producerFacet.filter((p) => p.producer_id === id).length })),
              { value: "__none__", label: "Atanmamış", count: producerFacet.filter((p) => !p.producer_id).length },
            ]}
          />
          <MultiFilter
            label="Üretim Durumu"
            testid="uretici-prod-status-filter"
            width="w-[190px]"
            totalCount={prodStFacet.length}
            selected={prodStSel}
            onChange={setProdStSel}
            options={[
              ...prodStatuses.map((s) => ({ value: s, label: s, count: prodStFacet.filter((p) => p.production_status === s).length })),
              { value: "__none__", label: "Atanmamış", count: prodStFacet.filter((p) => !p.production_status).length },
            ]}
          />
          {(q.trim() !== "" || zoneSel.length > 0 || hubSel.length > 0 || stSel.length > 0 || saleSel.length > 0 || standTypeSel.length > 0 || producerSel.length > 0 || prodStSel.length > 0 || areaSel.length > 0) && (
            <button
              onClick={() => { setQ(""); setZoneSel([]); setHubSel([]); setStSel([]); setSaleSel([]); setStandTypeSel([]); setProducerSel([]); setProdStSel([]); setAreaSel([]); }}
              data-testid="uretici-filters-clear"
              className="h-9 px-3 text-xs font-semibold text-slate-500 hover:text-slate-900 border border-transparent hover:border-slate-300 transition-colors duration-200"
            >
              Filtreleri temizle
            </button>
          )}
        </div>

        <AreaFilterButtons options={areaOptions} selected={areaSel} onChange={setAreaSel} testid="uretici-area-filter" />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="uretici-kpi-cards">
          <KpiCard icon={FolderOpen} label="Toplam Proje" value={visible.length} accent="bg-slate-900 text-white" testid="uretici-kpi-total" />
          <KpiCard icon={Ruler} label="Toplam m²" value={visible.reduce((s, p) => s + areaNum(p.area_m2), 0).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} accent="bg-amber-500/10 text-amber-600" testid="uretici-kpi-area" />
          <KpiCard icon={Box} label="Özel Stand" value={visible.filter((p) => p.project_status === "Özel").length} accent="bg-emerald-500/10 text-emerald-600" testid="uretici-kpi-ozel" active={stSel.length === 1 && stSel[0] === "Özel"} onClick={() => setStSel(stSel.length === 1 && stSel[0] === "Özel" ? [] : ["Özel"])} />
          <KpiCard icon={Boxes} label="SBB" value={visible.filter((p) => p.project_status === "SBB").length} accent="bg-blue-500/10 text-blue-600" testid="uretici-kpi-sbb" active={stSel.length === 1 && stSel[0] === "SBB"} onClick={() => setStSel(stSel.length === 1 && stSel[0] === "SBB" ? [] : ["SBB"])} />
        </div>

        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table ref={tableRef} className="w-full text-sm min-w-[1380px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-3 py-3 font-medium">#</th>
                <SortTh key="name" k="name">Proje</SortTh>
                <OrderedCells order={colOrder}>
                <SortTh key="zone" k="zone" hide={!v("zone")}>Bölge</SortTh>
                <SortTh key="stand_no" k="stand_no" hide={!v("stand_no")}>Stand</SortTh>
                <SortTh key="area" k="area" hide={!v("area")}>m²</SortTh>
                <SortTh key="type" k="type" hide={!v("type")}>Tür</SortTh>
                <SortTh key="stand_type" k="stand_type" hide={!v("stand_type")}>Stand Türü</SortTh>
                <SortTh key="tasarim" k="tasarim" hide={!v("tasarim")}>Tasarım</SortTh>
                <SortTh key="uretim" k="uretim" hide={!v("uretim")}>Üretim</SortTh>
                <SortTh key="baski" k="baski" hide={!v("baski")}>Baskı</SortTh>
                <SortTh key="icmal" k="icmal" hide={!v("icmal")}>İcmal</SortTh>
                <SortTh key="status" k="status" hide={!v("status")}>Statü</SortTh>
                <SortTh key="hub" k="hub" hide={!v("hub")}>Hub</SortTh>
                <SortTh key="sale" k="sale" hide={!v("sale")}>Süreç Durum</SortTh>
                <SortTh key="producer" k="producer" hide={!v("producer")}>Üretici</SortTh>
                <SortTh key="prod_status" k="prod_status" hide={!v("prod_status")}>Üretim Durumu</SortTh>
                <SortTh key="kurulum" k="kurulum" hide={!v("kurulum")}>Kurulum Tarihi</SortTh>
                <SortTh key="progress" k="progress" hide={!v("progress")}>İlerleme</SortTh>
                </OrderedCells>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={16} className="px-4 py-12 text-center text-slate-400" data-testid="uretici-empty">
                    {!loaded ? "Yükleniyor..." : term || zoneSel.length || hubSel.length || stSel.length || saleSel.length || standTypeSel.length || producerSel.length || prodStSel.length || areaSel.length ? "Filtrenizle eşleşen sonuç yok." : "Bölgenizde henüz proje yok."}
                  </td>
                </tr>
              ) : (
                sorted.map((p, i) => (
                  <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150" data-testid={`uretici-row-${p.id}`}>
                    <td className="px-3 py-3 text-slate-400 tabular-nums text-xs">{i + 1}</td>
                    <td className="px-4 py-3 font-semibold min-w-[220px]" data-testid={`uretici-name-${p.id}`}>{p.name}</td>
                    <OrderedCells order={colOrder}>
                    <td key="zone" className={cls("zone", "px-4 py-3")}><Badge variant="outline" className={`rounded-none text-xs ${zoneCls[p.zone] || "border-slate-300 text-slate-600"}`}>{p.zone}</Badge></td>
                    <td key="stand_no" className={cls("stand_no", "px-4 py-3 text-slate-500")}>{p.stand_no || "—"}</td>
                    <td key="area" className={cls("area", "px-4 py-3 text-slate-500 tabular-nums")}>{areaNum(p.area_m2) ? String(areaNum(p.area_m2)).replace(".", ",") : "—"}</td>
                    <td key="type" className={cls("type", "px-4 py-3 text-slate-500 text-xs")}>{p.project_type || "—"}</td>
                    <td key="stand_type" className={cls("stand_type", "px-4 py-3 text-slate-500 text-xs")}>{p.stand_type || <span className="text-slate-300">—</span>}</td>
                    <td key="tasarim" className={cls("tasarim", "px-4 py-3")}>
                      {baskiOnly ? <span className="text-slate-300 text-xs">—</span> : (
                        <DesignCell p={p} endpoint={`/uretici/projects/${p.id}`} onChanged={(files) => setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, files } : x)))} />
                      )}
                    </td>
                    <td key="uretim" className={cls("uretim", "px-4 py-3")}>
                      {baskiOnly ? <span className="text-slate-300 text-xs">—</span> : (
                      <DesignCell
                        p={p}
                        endpoint={`/uretici/projects/${p.id}`}
                        filesKey="uretim_files"
                        addKey="add_uretim_file_ids"
                        removeKey="remove_uretim_file_ids"
                        idp="uretici-uretim"
                        label="Üretim"
                        pdfImagesOnly
                        onChanged={(uretim_files) => setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, uretim_files } : x)))}
                      />
                      )}
                    </td>
                    <td key="baski" className={cls("baski", "px-4 py-3")}>
                      <DesignCell
                        p={p}
                        endpoint={`/uretici/projects/${p.id}`}
                        filesKey="baski_files"
                        addKey="add_baski_file_ids"
                        removeKey="remove_baski_file_ids"
                        idp="uretici-baski"
                        label="Baskı"
                        pdfImagesOnly
                        onChanged={(baski_files) => setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, baski_files } : x)))}
                      />
                    </td>
                    <td key="icmal" className={cls("icmal", "px-4 py-3")}>
                      {baskiOnly ? <span className="text-slate-300 text-xs">—</span> : (
                      <DesignCell
                        p={p}
                        endpoint={`/uretici/projects/${p.id}`}
                        filesKey="icmal_files"
                        addKey="add_icmal_file_ids"
                        removeKey="remove_icmal_file_ids"
                        idp="uretici-icmal"
                        label="İcmal"
                        pdfOnly
                        onChanged={(icmal_files) => setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, icmal_files } : x)))}
                      />
                      )}
                    </td>
                    <td key="status" className={cls("status", "px-4 py-3")}>
                      {p.project_status ? (
                        <span className={`inline-flex items-center h-7 px-2.5 text-xs font-semibold border ${statusColorCls(p.project_status)}`}>{p.project_status}</span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td key="hub" className={cls("hub", "px-4 py-3 text-slate-500 text-xs")}>{p.hub || <span className="text-slate-300">—</span>}</td>
                    <td key="sale" className={cls("sale", "px-4 py-3")}>
                      {p.sale_status ? (
                        <span className={`inline-flex items-center h-7 px-2.5 text-xs font-semibold border ${saleStatusCls(p.sale_status)}`}>{p.sale_status}</span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td key="producer" className={cls("producer", "px-4 py-3")} data-testid={`uretici-producer-${p.id}`}>
                      {p.producer_name ? (
                        <span className="font-semibold text-amber-700 text-xs">{p.producer_name}</span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td key="prod_status" className={cls("prod_status", "px-4 py-3")}>
                      {baskiOnly ? (
                        p.production_status
                          ? <span className={`inline-flex items-center h-7 px-2.5 text-xs font-semibold border ${prodStatusCls(p.production_status)}`}>{p.production_status}</span>
                          : <span className="text-slate-300 text-xs">—</span>
                      ) : (
                        <ProductionStatusCell p={p} statuses={prodStatuses} onSave={setProductionStatus} onAddNew={(proj) => setProdStatusAddFor(proj)} idp="uretici-prod-status" />
                      )}
                    </td>
                    <td key="kurulum" className={cls("kurulum", "px-4 py-3")}>
                      {isMine(p) ? (
                        <KurulumDateCell p={p} onSave={saveKurulumDate} />
                      ) : (
                        <span className={`text-xs tabular-nums ${p.kurulum_tarihi ? "font-semibold text-slate-700" : "text-slate-300"}`}>{p.kurulum_tarihi || "—"}</span>
                      )}
                    </td>
                    <td key="progress" className={cls("progress", "px-4 py-3")}>
                      {(() => {
                        const pct = progressPct(p);
                        const bar = (
                          <>
                            <div className="w-14 h-1.5 bg-slate-100 overflow-hidden">
                              <div className={`h-full transition-all duration-300 ${pct === 100 ? "bg-emerald-500" : pct > 0 ? "bg-blue-500" : ""}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`text-xs font-semibold tabular-nums ${pct === 100 ? "text-emerald-600" : pct > 0 ? "text-slate-700" : "text-slate-300"}`}>%{pct}</span>
                          </>
                        );
                        return isMine(p) ? (
                          <button type="button" onClick={() => setProgressFor(p)} data-testid={`uretici-progress-btn-${p.id}`} title="Kurulum ilerlemesini işaretle" className="flex items-center gap-2 min-w-[96px] hover:opacity-80 transition-opacity duration-150">
                            {bar}
                            <ListChecks size={13} className="text-slate-400" />
                          </button>
                        ) : (
                          <div className="flex items-center gap-2 min-w-[96px]">{bar}</div>
                        );
                      })()}
                    </td>
                    </OrderedCells>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={!!progressFor} onOpenChange={(o) => !o && setProgressFor(null)}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="uretici-progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ListChecks size={18} /> {progressFor?.name}
            </DialogTitle>
          </DialogHeader>
          {progressFor && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-slate-500">Kurulum İlerlemesi</span>
                  <span className={`font-bold tabular-nums ${progressPct(progressFor) === 100 ? "text-emerald-600" : "text-slate-900"}`} data-testid="uretici-progress-dialog-pct">
                    %{progressPct(progressFor)}
                  </span>
                </div>
                <div className="h-2 bg-slate-100 overflow-hidden">
                  <div className={`h-full transition-all duration-300 ${progressPct(progressFor) === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${progressPct(progressFor)}%` }} />
                </div>
              </div>
              <div className="space-y-1 max-h-[55vh] overflow-y-auto">
                {PROGRESS_ITEMS.map((item, i) => {
                  const checked = (progressFor.kurulum_progress || []).includes(item);
                  return (
                    <label
                      key={item}
                      className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors duration-150 ${checked ? "bg-emerald-50/60 border-emerald-200" : "bg-white border-slate-200 hover:border-slate-300"}`}
                      data-testid={`uretici-progress-item-${i}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleProgress(progressFor, item)} className="rounded-none" data-testid={`uretici-progress-check-${i}`} />
                      <span className={`text-sm ${checked ? "text-slate-800 font-medium" : "text-slate-600"}`}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ProductionStatusAddDialog
        open={!!prodStatusAddFor}
        onOpenChange={(o) => !o && setProdStatusAddFor(null)}
        onCreated={(st, list) => { setProdStatuses(list); if (prodStatusAddFor) setProductionStatus(prodStatusAddFor, st); setProdStatusAddFor(null); }}
      />
    </div>
  );
}
